/**
 * Zestaw sekcji KARTOTEKI WŁASNEJ kokpitu decyzji („Grupuj według: Klasa / kartoteka") — klucz
 * grupy `custom.<slug>`. Trzeci rodzaj zestawu obok kartotek z katalogu pól (`section-kits.tsx`) i
 * stałych dokumentów Handlu (`handel-section-kits.tsx`).
 *
 * Kolumny sekcji = kolumny LISTY tej kartoteki (`ModuleRecordsPage`): kolumny systemowe (nazwa,
 * numer, kategoria) + pola „pokaż na liście", z tym samym wierszem filtrów per typ pola i tymi
 * samymi komórkami — wszystko ze wspólnego buildera `lib/custom-modules/module-list-columns.tsx`,
 * więc lista i sekcja nie rozjadą się. Widoczność, kolejność i szerokość nakłada układ listy
 * administratora (tylko do odczytu).
 *
 * Różnice względem list z katalogu pól:
 *  - katalog (pola, układ, kategorie) NIE jest pobierany osobno — decydujący może nie mieć prawa
 *    do kartoteki, więc serwer dołącza go do odpowiedzi sekcji (`columns`, patrz
 *    `CustomSectionColumns`); sekcja ma kolumny dopiero po pierwszej odpowiedzi,
 *  - pełne pola niezależnie od reguł widoczności kartoteki; pól „Hasło" i „Ukryte" serwer nie
 *    zwraca, a panel dodatkowo odrzuca je po typie (`SECTION_SECRET_FIELD_TYPES`), tak samo jak
 *    pola o slugu kolidującym z kluczami wiersza sekcji (`SECTION_ROW_RESERVED_KEY_SET`),
 *  - sortować wolno tylko po slugach z `meta.sortable`, filtrować tylko po slugach z
 *    `meta.filterable` (kolumna bez filtra po stronie serwera dostaje pustą komórkę filtra),
 *  - wiersz „Razem" pod każdą kolumną pola „Kwota (PLN)" (`currency`), sformatowany jak komórki
 *    tego pola (tekst `<kwota> PLN`), ale z DOKŁADNEJ sumy serwera — nie jak kwoty dokumentów
 *    (`formatMoney`),
 *  - lista kartoteki nie ma kolumny „Firma", więc sekcja jej nie ma (ani filtra „Firma", ani
 *    prośby o `companyOptions`).
 */

// Rejestracja typów pól (`FieldRegistry`) — bez niej komórki rysowałyby surowy tekst.
import '@/components/fields/types';

import type { ReactNode } from 'react';
import { emptyListConfig, SECTION_ROW_RESERVED_KEYS } from '@saas/shared';
import { replaceEqualDeep } from '@tanstack/react-query';
import type { TFunction } from 'i18next';

import type { ColumnDefinition, TableLayoutConfig } from '@/components/configurable-table';
import type { ModuleField } from '@/components/fields/FieldRegistry';
import {
  buildModuleListColumns,
  type ModuleListFilterOptions,
  type ModuleListRow,
} from '@/lib/custom-modules/module-list-columns';
import type { CurrencySum, CustomSectionColumns } from '@/lib/processes/approvals-api';
import { formatMoney } from '@/lib/processes/format-money';
import { CUSTOM_MODULE_CODE_PREFIX } from '@/lib/processes/module-routes';

import { type CustomSectionKit, type DecisionSectionRow, NO_SUM_SLUGS } from './section-kits';

/**
 * Wiersz sekcji kartoteki własnej: wiersz sekcji (`id` + kroki decyzji) i wartości pól pod slugami
 * (jak w wierszu listy kartoteki własnej — `ModuleListRow`). Na tym typie wspólny builder listy
 * kartoteki własnej buduje kolumny sekcji bez rzutu; `DecisionSectionRow` nie ma sygnatury indeksu.
 */
export type CustomSectionRow = DecisionSectionRow & ModuleListRow;

/**
 * Typy pól, których panel NIGDY nie pokazuje w sekcji: hasło i pole ukryte. Serwer ich nie zwraca
 * (ani w katalogu, ani w wierszu) — to drugi strażnik na wypadek błędu kontraktu.
 */
const SECTION_SECRET_FIELD_TYPES: ReadonlySet<string> = new Set(['password', 'hidden']);

/**
 * Klucze wiersza sekcji, które należą do panelu (`id` rekordu, `decisions` — kroki decyzji): pole
 * kartoteki o takim slugu nadpisałoby je albo je zasłoniło (kolumna czytałaby kroki decyzji
 * zamiast wartości pola), więc panel go nie pokazuje. Lista kluczy wspólna z serwerem
 * (`SECTION_ROW_RESERVED_KEYS` z `@saas/shared`; serwer buduje z niej `SECTION_RESERVED_FIELD_SLUGS`).
 */
const SECTION_ROW_RESERVED_KEY_SET: ReadonlySet<string> = new Set<string>(
  SECTION_ROW_RESERVED_KEYS,
);

/** Typ pola sumowanego w wierszu „Razem" — wyłącznie kwota „Kwota (PLN)". */
const SUMMABLE_FIELD_TYPE = 'currency';

/** Sufiks kwoty pola `currency` — ten sam co w komórkach pola (`CurrencyFieldType`). */
const CURRENCY_FIELD_SUFFIX = 'PLN';

/** Zestawy sekcji kartotek własnych wg klucza grupy — jedna tożsamość na klucz (patrz niżej). */
const CUSTOM_SECTION_KITS = new Map<string, CustomSectionKit>();

/**
 * Zestaw sekcji dla klucza grupy `custom.<slug>` albo `undefined` (klucz nie jest kartoteką własną
 * albo ma pusty slug). Slug nie jest walidowany: autorytetem jest serwer — zły slug kończy się
 * odpowiedzią 400 `UNKNOWN_SECTION`, którą sekcja pokazuje jako zwykły błąd wczytania.
 *
 * Ten sam klucz zwraca ZAWSZE ten sam obiekt (jak `SECTION_KITS` i rejestr Handlu): zestaw jest
 * zależnością memoizacji kolumn tabeli sekcji, a nowy obiekt przy każdym renderze przebudowywałby
 * kolumny i zerował ich konfigurację (ukryte kolumny, szerokości). Pamięć zestawów jest
 * ograniczona liczbą kartotek własnych tenanta (jedna aplikacja = jeden tenant), więc cache nie
 * wymaga czyszczenia.
 */
export function getCustomSectionKit(groupKey: string): CustomSectionKit | undefined {
  if (!groupKey.startsWith(CUSTOM_MODULE_CODE_PREFIX)) return undefined;
  if (groupKey.length === CUSTOM_MODULE_CODE_PREFIX.length) return undefined;
  let kit = CUSTOM_SECTION_KITS.get(groupKey);
  if (kit === undefined) {
    kit = { source: 'custom', targetModuleCode: groupKey };
    CUSTOM_SECTION_KITS.set(groupKey, kit);
  }
  return kit;
}

/**
 * Czy wartość z odpowiedzi sekcji jest użytecznym katalogiem kartoteki własnej: obiekt z listami
 * pól i kategorii, flagą numeracji (`hasNumbering`, boolean — builder kolumn czyta je wszystkie) i
 * układem listy będącym obiektem, `null` albo nieobecnym. Serwer wysyła katalog zawsze dla klucza
 * `custom.<slug>`, więc brak go (`undefined`), `null` albo katalog o złamanym kształcie to złamany
 * kontrakt — wołający pokazuje wtedy jawny błąd sekcji zamiast wyjątku w renderze.
 */
export function isCustomSectionCatalog(value: unknown): value is CustomSectionColumns {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<Record<keyof CustomSectionColumns, unknown>>;
  const layout = candidate.layout;
  return (
    Array.isArray(candidate.fields) &&
    Array.isArray(candidate.categories) &&
    typeof candidate.hasNumbering === 'boolean' &&
    (layout === undefined || layout === null || typeof layout === 'object')
  );
}

/**
 * Katalog z nowej odpowiedzi sekcji z zachowaniem tożsamości części, których treść się nie
 * zmieniła: każde zapytanie o inną stronę, sort lub filtr to nowy klucz zapytania, więc odpowiedź
 * niesie świeżą KOPIĘ katalogu. Bez tego każdy ruch w tabeli przebudowywałby kolumny i układ,
 * a konfiguracja kolumn tabeli (ukryte kolumny, szerokości) wracałaby do domyślnej. Części
 * (`fields`, `categories`, `layout`, `hasNumbering`) są porównywane OSOBNO (`replaceEqualDeep`
 * zwraca poprzednią część, gdy treść się zgadza): równa treść wszystkich → poprzedni katalog;
 * zmieniona tylko jedna część (np. kategorie) → nowy katalog złożony z nowej części i poprzednich
 * pozostałych (np. `fields` zostają tym samym obiektem, więc kolumny się nie przebudowują).
 */
export function stabilizeCustomSectionCatalog(
  previous: CustomSectionColumns,
  next: CustomSectionColumns,
): CustomSectionColumns {
  const fields = replaceEqualDeep(previous.fields, next.fields);
  const categories = replaceEqualDeep(previous.categories, next.categories);
  const layout = replaceEqualDeep(previous.layout, next.layout);
  const { hasNumbering } = next;
  if (
    fields === previous.fields &&
    categories === previous.categories &&
    layout === previous.layout &&
    hasNumbering === previous.hasNumbering
  ) {
    return previous;
  }
  return { fields, categories, layout, hasNumbering };
}

/**
 * Pola sekcji pokazywane jako kolumny: „pokaż na liście" (jak domyślne kolumny listy kartoteki),
 * bez „Hasło" i „Ukryte" oraz bez pól o slugu kolidującym z kluczami wiersza sekcji (`id`,
 * `decisions`). Kolejność jak w katalogu (kolejność pól w kreatorze).
 */
export function customSectionListFields(fields: ReadonlyArray<ModuleField>): ModuleField[] {
  return fields.filter(
    (field) =>
      field.showInList &&
      !SECTION_SECRET_FIELD_TYPES.has(field.fieldType) &&
      !SECTION_ROW_RESERVED_KEY_SET.has(field.slug),
  );
}

/**
 * Układ listy z katalogu sekcji jako konfiguracja tabeli (puste pola uzupełnione wartościami
 * pustymi) — ta sama konwersja co układ listy sekcji z katalogu pól (`useSectionListLayout`).
 * Brak układu (`undefined` / `null`) = pusta lista kolumn = domyślna widoczność wszystkich kolumn.
 * `defaultSort` układu NIE jest stosowany do zapytania (strona listy kartoteki też go nie
 * stosuje — bez kliknięcia w nagłówek serwer sortuje po pozycji i dacie utworzenia).
 */
export function customSectionListConfig(
  layoutListConfig: Partial<TableLayoutConfig> | null | undefined,
): TableLayoutConfig {
  return { ...emptyListConfig, ...(layoutListConfig ?? {}) };
}

/** Wejście budowy kolumn sekcji kartoteki własnej. */
export interface CustomSectionColumnsInput {
  t: TFunction;
  /** Katalog z ostatniej odpowiedzi sekcji; `undefined` = jeszcze nie przyszedł (brak kolumn). */
  catalog: CustomSectionColumns | undefined;
  /** `meta.sortable` ostatniej odpowiedzi (`undefined` = jeszcze nie przyszła). */
  sortableSlugs: readonly string[] | undefined;
  /** `meta.filterable` ostatniej odpowiedzi (`undefined` = jeszcze nie przyszła). */
  filterableSlugs: readonly string[] | undefined;
  /** Wczytane opcje filtra: użytkownicy, relacje (z rekordów sekcji) i pozycje słowników. */
  filterOptions: ModuleListFilterOptions;
}

/** Wynik budowy kolumn sekcji kartoteki własnej. */
export interface CustomSectionBuilt {
  columns: ColumnDefinition<DecisionSectionRow>[];
  /** Id kolumn z sumą „Razem" = slugi pól „Kwota (PLN)" (równe kluczom `sums` odpowiedzi). */
  sumSlugs: readonly string[];
  /** Sformatowana suma kolumny: jak komórki tego pola, a bez pola — kwota z kodem waluty. */
  formatSum: (columnId: string, sum: CurrencySum) => ReactNode;
}

/** Kolumna bez kontrolki filtra, gdy serwer nie filtruje po jej polu (spoza `meta.filterable`). */
function withServerFilter<TRow extends ModuleListRow>(
  column: ColumnDefinition<TRow>,
  filterable: ReadonlySet<string>,
): ColumnDefinition<TRow> {
  const slug = column.headerFilter?.fieldSlug;
  if (slug === undefined || filterable.has(slug)) return column;
  return { ...column, headerFilter: undefined, filterable: false };
}

/**
 * Buduje kolumny sekcji kartoteki własnej z katalogu odpowiedzi (patrz nagłówek pliku). Bez
 * katalogu — brak kolumn (sekcja czeka na pierwszą odpowiedź).
 */
export function buildCustomSectionColumns(input: CustomSectionColumnsInput): CustomSectionBuilt {
  const { t, catalog, sortableSlugs, filterableSlugs, filterOptions } = input;
  if (catalog === undefined) {
    return { columns: [], sumSlugs: NO_SUM_SLUGS, formatSum: formatSumAsMoney };
  }

  const fields = customSectionListFields(catalog.fields);
  const filterable = new Set(filterableSlugs ?? []);
  // Wiersz sekcji ma kształt wiersza listy kartoteki + `decisions`, a tabela sekcji czyta z niego
  // `id` i `decisions`; resztę czytają kolumny — builder jest ogólny po typie wiersza (`CustomSectionRow`).
  const columns = buildModuleListColumns<CustomSectionRow>({
    t,
    fields,
    categories: catalog.categories,
    hasNumbering: catalog.hasNumbering,
    sortableSlugs: sortableSlugs ?? [],
    filterOptions,
  }).map((column) => withServerFilter(column, filterable));

  const currencySlugs = fields
    .filter((field) => field.fieldType === SUMMABLE_FIELD_TYPE)
    .map((field) => field.slug);
  const currencySlugSet = new Set(currencySlugs);
  return {
    // JEDYNY rzut typów w tym zestawie — granica rejestru sekcji: kolumny są zbudowane na typie
    // wiersza kartoteki własnej (`CustomSectionRow` = wiersz sekcji + wartości pól pod slugami), a
    // tabela sekcji trzyma je pod wspólnym typem `DecisionSectionRow`. Bezpieczne, bo to ten sam
    // wiersz widziany szerzej: tabela czyta z niego wyłącznie `id` i `decisions`, resztę kolumny.
    columns: columns as ColumnDefinition<DecisionSectionRow>[],
    sumSlugs: currencySlugs,
    formatSum: (columnId, sum) => {
      if (!currencySlugSet.has(columnId)) return formatSumAsMoney(columnId, sum);
      // Suma to dokładny tekst dziesiętny z serwera (dwa miejsca po przecinku) — bez konwersji
      // na liczbę zmiennoprzecinkową, która zaokrąglałaby duże kwoty.
      return `${sum.total} ${CURRENCY_FIELD_SUFFIX}`;
    },
  };
}

function formatSumAsMoney(_columnId: string, sum: CurrencySum): ReactNode {
  return formatMoney(sum.total, sum.currencyCode);
}
